import { useTranslation } from 'react-i18next';
import type { ActionItem } from '@/mocks/dashboard';

interface Props {
  items: ActionItem[];
}

const priorityConfig = {
  high: { labelColor: 'text-warm-700 bg-warm-50 border-warm-200', icon: 'ri-error-warning-line' },
  medium: { labelColor: 'text-ink-600 bg-ink-50 border-ink-200', icon: 'ri-alert-line' },
  low: { labelColor: 'text-ink-400 bg-ink-50 border-ink-200', icon: 'ri-information-line' },
};

export default function DashboardActionBanner({ items }: Props) {
  const { t } = useTranslation('dashboard');
  if (!items.length) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <i className="ri-notification-badge-line text-warm-500 text-lg" />
        <h2 className="text-base md:text-lg font-semibold text-ink-600">
          {t('dashboard.actionRequired')}
        </h2>
        <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-warm-500 text-white text-xs font-bold">
          {items.length}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((item) => {
          const cfg = priorityConfig[item.priority];
          return (
            <a
              key={item.id}
              href={item.link}
              className="flex items-start gap-3 rounded-xl border border-ink-100 bg-white p-3 md:p-4 hover:border-warm-300 hover:shadow-sm transition-all"
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${cfg.labelColor}`}>
                <i className={`${cfg.icon} text-base`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink-600 leading-relaxed">{item.message}</p>
              </div>
              <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${cfg.labelColor}`}>
                {t(`dashboard.actionPriority.${item.priority}`)}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}